import { FC, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { Context } from "../../index";
import "./ProjectsMain.css";
import { observer } from "mobx-react-lite";
import "../../pages/Project/Project.css";
import ProjectLogo from "../../assets/default__background.jpg";
import ProjectFrameNone from "../../assets/projects_none.svg";
import { useNavigate } from "react-router-dom";
import FunctionSystem from "../../models/functionCode/functionsSystem";
import ProjectsToolbar from "./ProjectsToolbar";
import ProjectCard from "./ProjectCard";
import Search from "../UiLib/Search/Search";
import { ICardProject } from "../../common/api_interfaces/project/ICardProjectProject";
import { FunctionsAccessRequest } from "../../common/api_interfaces/role/FunctionsAccessRequest";
import { personalSettings } from "../../common/enums/personalSettings";

interface ProjectsMainProps {
    loadArchivedProjects?: boolean;
}

const ProjectsMain: FC<ProjectsMainProps> = ({ loadArchivedProjects }) => {
    const { store } = useContext(Context);
    const [hrefCoverCompany, setHrefCoverCompany] = useState<string>(ProjectLogo);
    const [projects, setProjects] = useState<ICardProject[]>([]);
    const navigate = useNavigate();

    const [viewProjects, setViewProjects] = useState(false);
    const [addProject, setAddProject] = useState(false);
    const [projectSearchValue, setProjectSearchValue] = useState<string>("");

    const loadCover = useCallback(async () => {
        try {
            const picture = await store.commonService.getSystemCover();
            setHrefCoverCompany(picture.data?.length > 0 ? picture.data : ProjectLogo);

            const functionClass = new FunctionSystem();
            const newCheckRole: FunctionsAccessRequest = {
                projectId: null,
                boardId: null,
                functionCodes: [...functionClass.getSystemFunction, ...functionClass.getProjectFunction],
            };

            const systemAccess = await store.hasPermission(newCheckRole);

            systemAccess.forEach((xx) => {
                if (xx.functionCode === "SystemProjectAction") {
                    const canView = xx.permissions.some((perm) => perm.permissionCode === "view" && perm.isGranted);
                    setViewProjects(canView);
                    if (!canView) {
                        navigate("/noAccessPages");
                    }
                }
                if (xx.functionCode === "ProjectAction") {
                    setAddProject(xx.permissions.some((perm) => perm.permissionCode === "create" && perm.isGranted));
                }
            });
        } catch (err) {
            console.error("Failed to load cover:", err);
            setHrefCoverCompany(ProjectLogo);
        }
    }, [store, navigate]);

    const loadData = useCallback(async () => {
        try {
            if (loadArchivedProjects) {
                await store.getArchiveProjects();
            } else {
                const includeSubprojects =
                    (store.personalSettings.find((s) => s.paramName === personalSettings.showSubprojectsProjectPage)
                        ?.value as boolean) ?? true;
                await store.getAllProject(includeSubprojects);
            }
            
            if (store.projects) {
                setProjects(store.projects);
            }
        } catch (error) {
            console.error("Failed to load projects:", error);
        }
    }, [store, loadArchivedProjects]);

    useEffect(() => {
        loadData();
    }, [loadData, loadArchivedProjects]);

    useEffect(() => {
        loadCover();
    }, [loadCover]);

    const handleSearch = useCallback((text: string) => {
        setProjectSearchValue(text);
    }, []);

    const filteredProjects = useMemo(() => {
        if (!projectSearchValue) return projects;
        
        const searchLower = projectSearchValue.toLowerCase();
        return projects.filter(project => 
            project.shortDescription?.toLowerCase().includes(searchLower)
        );
    }, [projects, projectSearchValue]);

    const hasProjects = filteredProjects.length > 0;
    const showNoProjects = projects.length === 0;
    const showFilteredProjects = hasProjects && projectSearchValue.length > 0;
    const showAllProjects = hasProjects && projectSearchValue.length === 0;

    return (
        <div className="projects_component">
            <div className="projects_toolbar_container">
                <div className="projects-toolbar">
                    <ProjectsToolbar loadData={loadData} addProject={addProject} />
                    <Search value={projectSearchValue} changeSearchValue={handleSearch} />
                </div>
            </div>
            
            <div className="projects_main_container">
                <div className="project_overflow_container">
                    {showNoProjects ? (
                        <div className="projects_none_container">
                            <img src={ProjectFrameNone} alt="No projects" />
                            <h1>У вас пока нет ни одного проекта</h1>
                        </div>
                    ) : (
                        <>
                            {showFilteredProjects && filteredProjects.map((project) => (
                                <div key={project.id} className="projects_card_container">
                                    <ProjectCard
                                        project={project}
                                        hrefCoverCompany={hrefCoverCompany}
                                        viewProjects={viewProjects}
                                    />
                                </div>
                            ))}
                            
                            {showAllProjects && projects.map((project) => (
                                <div key={project.id} className="projects_card_container">
                                    <ProjectCard
                                        project={project}
                                        hrefCoverCompany={hrefCoverCompany}
                                        viewProjects={viewProjects}
                                    />
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default observer(ProjectsMain);